import { useState, useMemo } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { authorizedFetch } from "@/lib/api"
import { API_URL } from "@/lib/mutation-utils"

// Minimum shape a TripRow must satisfy for the return-to-base feature.
export interface ReturnToBaseTripBase {
  id:                  string
  internalId:          number
  driverId:            string | null
  driverName:          string | null
  truckLicensePlate:   string | null
  trailerLicensePlate: string | null
  destinationCityId:   string | null
  destinationCityName: string | null
  departureAt:              string | null
  arrivalAt:               string | null
  scheduledOriginArrivalAt: string | null
}

export interface ReturnToBasePreview {
  trip:             ReturnToBaseTripBase
  originCityName:   string | null
  baseCityName:     string | null
  departureAt:      string | null
  arrivalAt:        string | null
  routeKm:          number | null
  isLoading:        boolean
  // Non-null when the trip cannot be used for return-to-base — shown in dialog instead of form.
  blockReason:      string | null
}

export interface UseReturnToBaseOptions {
  basePartyId:      string        // from app config — used to check configuration
  baseCityId:       string | null // resolved city UUID — used for city equality check
  baseCityName:     string | null // display name
  averageSpeedKmh?: number        // displayed in tooltip; calculation is server-side
  invalidateKeys:   unknown[][]
  onSuccess?:       () => void
}

// Level 1: can the button open the dialog?
// Requires exactly 1 trip selected (selectedIds.size === 1).
function deriveCandidateTrip(
  allTrips: ReturnToBaseTripBase[],
  selectedIds: Set<string>,
): ReturnToBaseTripBase | null {
  if (selectedIds.size !== 1) return null
  const [id] = selectedIds
  return allTrips.find(t => t.id === id) ?? null
}

// Level 2: given a candidate trip, is there a reason it cannot create a return trip?
export function getBlockReason(
  trip: ReturnToBaseTripBase,
  options: UseReturnToBaseOptions,
): string | null {
  if (!options.basePartyId || !options.baseCityId) {
    return "La ciudad base no está configurada en app.config.json (trips.basePartyId)."
  }
  if (!trip.destinationCityId) {
    return `El viaje #${trip.internalId} no tiene ciudad de destino configurada.`
  }
  // Accept scheduledOriginArrivalAt as a valid date source, falling back to departureAt.
  const hasAnyDate = trip.arrivalAt || trip.departureAt || trip.scheduledOriginArrivalAt
  if (!hasAnyDate) {
    return `El viaje #${trip.internalId} no tiene fecha de salida, llegada ni llegada proyectada. No es posible calcular las fechas del viaje de regreso.`
  }
  if (trip.destinationCityId === options.baseCityId) {
    return `El viaje #${trip.internalId} ya tiene como destino la base (${options.baseCityName ?? "base"}). No es necesario crear un viaje de regreso.`
  }
  return null
}

export function useReturnToBase(
  trips: ReturnToBaseTripBase[],
  selectedIds: Set<string>,
  options: UseReturnToBaseOptions,
) {
  const qc = useQueryClient()
  const [preview, setPreview] = useState<ReturnToBasePreview | null>(null)

  // Non-null when exactly 1 trip is selected → enables the button.
  const returnToBaseTrip = useMemo(
    () => deriveCandidateTrip(trips, selectedIds),
    [trips, selectedIds],
  )

  async function openPreview() {
    if (!returnToBaseTrip) return
    const trip = returnToBaseTrip

    const blockReason = getBlockReason(trip, options)

    if (blockReason) {
      setPreview({
        trip,
        originCityName:  trip.destinationCityName,
        baseCityName:    null,
        departureAt:     null,
        arrivalAt:       null,
        routeKm:         null,
        isLoading:       false,
        blockReason,
      })
      return
    }

    setPreview({
      trip,
      originCityName:  trip.destinationCityName,
      baseCityName:    null,  // resolved from API response
      departureAt:     null,
      arrivalAt:       null,
      routeKm:         null,
      isLoading:       true,
      blockReason:     null,
    })

    try {
      const res = await authorizedFetch(`${API_URL}/api/trips/return-to-base/preview`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ tripId: trip.id }),
      })
      const data = await res.json() as {
        originCityName: string | null
        baseCityName:   string | null
        departureAt:    string | null
        arrivalAt:      string | null
        routeKm:        number | null
      }
      setPreview({
        trip,
        originCityName:  data.originCityName,
        baseCityName:    data.baseCityName,
        departureAt:     data.departureAt,
        arrivalAt:       data.arrivalAt,
        routeKm:         data.routeKm,
        isLoading:       false,
        blockReason:     null,
      })
    } catch {
      setPreview(null)
      toast.error("No se pudo cargar el preview de volver a base")
    }
  }

  async function confirmReturnToBase() {
    if (!preview || preview.blockReason) return
    const { trip } = preview
    setPreview(prev => prev ? { ...prev, isLoading: true } : null)
    try {
      const res = await authorizedFetch(`${API_URL}/api/trips/return-to-base`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ tripId: trip.id }),
      })
      const body = await res.json() as { id?: string; internalId?: number; error?: string }
      if (!res.ok) throw new Error(body.error ?? "Error al crear el viaje")

      for (const key of options.invalidateKeys) {
        qc.invalidateQueries({ queryKey: key })
      }
      setPreview(null)
      options.onSuccess?.()
      toast.success(
        body.internalId ? `Viaje #${body.internalId} creado` : "Viaje de regreso a base creado",
        { description: "Volver a base" },
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al crear el viaje")
      setPreview(prev => prev ? { ...prev, isLoading: false } : null)
    }
  }

  // Closes the dialog. No-op while a creation is in flight.
  function closePreview() {
    if (preview?.isLoading) return
    setPreview(null)
  }

  return {
    // Non-null when exactly 1 trip is selected → enables the button.
    returnToBaseTrip,
    // Non-null while the dialog is open.
    returnToBasePreview: preview,
    openPreview,
    confirmReturnToBase,
    closePreview,
  }
}
